import { useState } from "react";
import {
    Add,
    Category,
    DataObject,
    DataObjectOutlined,
    Delete,
    DeviceHub,
    Edit,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    useTheme,
} from "@mui/material";

import api from "../../../services/api";
import InternoModalConfirmacaoSaida from "../../../components/modalSaindoDaPagina";
import CardSelection from "../cardSelection";
import { hasSchema } from "../../../utils/dashboardSchemaDraft";

/**
 * Configuração dos 3 níveis da árvore Templates.
 * Cada nível descreve onde está o nome do nó, qual a coleção de filhos,
 * quais endpoints de API usar e como rotular os botões.
 */
export const LEVELS = [
    {
        name: "fabricante",
        childrenKey: "Funcoes",
        editLabel: "Fabricante",
        // tom para ícones, conectores e barra do card
        colorKey: "blue",
        apiUpdate: "/putFabricante",
        apiUpdateBody: (text, id) => ({ fabricante: text, id }),
        apiDelete: "/deleteFabricante",
        deleteText:
            "Esta ação não pode ser desfeita e todos os dados relacionados a este fabricante serão perdidos.",
        // criação de filho (Função)
        childCreateLabel: "Função",
        apiCreateChild: "/postFuncao",
        apiCreateChildBody: (text, parentId) => ({
            funcao: text,
            fabricanteId: parentId,
        }),
        transformNewChild: (data) => ({ ...data, Modelos: [] }),
    },
    {
        name: "funcao",
        childrenKey: "Modelos",
        editLabel: "Função",
        colorKey: "purple",
        apiUpdate: "/putFuncao",
        apiUpdateBody: (text, id) => ({ funcao: text, id }),
        apiDelete: "/deleteFuncao",
        deleteText:
            "Esta ação não pode ser desfeita. Todos os modelos associados a esta função também serão excluídos.",
        // criação de filho (Modelo)
        childCreateLabel: "Modelo",
        apiCreateChild: "/postModelo",
        apiCreateChildBody: (text, parentId) => ({
            modelo: text,
            funcaoId: parentId,
        }),
        transformNewChild: (data) => data,
    },
    {
        name: "modelo",
        childrenKey: null, // folha
        editLabel: "Modelo",
        colorKey: "green",
        apiUpdate: "/putModelo",
        apiUpdateBody: (text, id) => ({ modelo: text, id }),
        apiDelete: "/deleteModelo",
        deleteText: "Esta ação não pode ser desfeita.",
        isLeaf: true,
    },
];

/* -------------------------------------------------------------------------- */
/*  Helpers de mutação na árvore (executados no root via setFabricantes)      */
/* -------------------------------------------------------------------------- */function mapAtLevel(tree, levelIdx, id, mapper) {
    if (levelIdx === 0) {
        return tree.map((n) => (n.id === id ? mapper(n) : n));
    }
    return tree.map((fab) => ({
        ...fab,
        Funcoes:
            levelIdx === 1
                ? (fab.Funcoes || []).map((fun) =>
                    fun.id === id ? mapper(fun) : fun
                )
                : (fab.Funcoes || []).map((fun) => ({
                    ...fun,
                    Modelos: (fun.Modelos || []).map((mod) =>
                        mod.id === id ? mapper(mod) : mod
                    ),
                })),
    }));
}

export const treeMutators = (setFabricantes) => ({
    updateNode: (levelIdx, id, patch) =>
        setFabricantes((prev) =>
            mapAtLevel(prev, levelIdx, id, (n) => ({ ...n, ...patch }))
        ),
    removeNode: (levelIdx, id) =>
        setFabricantes((prev) => {
            const placeholder = {
                id: null,
                [LEVELS[levelIdx].name]: null,
                ...(LEVELS[levelIdx].childrenKey
                    ? { [LEVELS[levelIdx].childrenKey]: [] }
                    : { Predefinicoes: [] }),
            };
            return mapAtLevel(prev, levelIdx, id, () => placeholder);
        }),
    addChild: (parentLevelIdx, parentId, child) => {
        if (parentLevelIdx < 0) {
            setFabricantes((prev) => [...prev, child]);
            return;
        }
        const childKey = LEVELS[parentLevelIdx].childrenKey;
        setFabricantes((prev) =>
            mapAtLevel(prev, parentLevelIdx, parentId, (n) => ({
                ...n,
                [childKey]: [...(n[childKey] || []), child],
            }))
        );
    },
});

/* -------------------------------------------------------------------------- */
/*  Componente recursivo                                                      */
/* -------------------------------------------------------------------------- */

function hasSelectedDescendant(node, levelIdx, selectedId) {
    const level = LEVELS[levelIdx];
    if (!level.childrenKey) return false;
    const children = node[level.childrenKey] || [];
    const childLevel = LEVELS[levelIdx + 1];
    return children.some((c) => {
        if (childLevel.isLeaf) return c?.Predefinicoes?.id === selectedId;
        return hasSelectedDescendant(c, levelIdx + 1, selectedId);
    });
}

function getNodeColor(theme) {
    return (
        theme.palette.colors?.blue?.light ||
        theme.palette.colors?.green?.light
    );
}

function getLevelColor(theme, levelIdx) {
    const key = LEVELS[levelIdx].colorKey || "green";
    return (
        theme.palette.colors?.[key]?.light ||
        theme.palette.colors?.green?.light
    );
}

function collectStats(node, levelIdx) {
    const stats = { funcoes: 0, modelos: 0, comSchema: 0, semSchema: 0 };
    if (levelIdx === 2) {
        // Modelo (folha) — única contagem é o próprio schema
        if (hasSchema(node)) stats.comSchema = 1;
        else stats.semSchema = 1;
        return stats;
    }
    if (levelIdx === 1) {
        // Função — itera Modelos
        for (const m of node.Modelos || []) {
            if (m?.modelo == null) continue;
            stats.modelos += 1;
            if (hasSchema(m)) stats.comSchema += 1;
            else stats.semSchema += 1;
        }
        return stats;
    }
    // Fabricante — itera Funcoes -> Modelos
    for (const f of node.Funcoes || []) {
        if (f?.funcao == null) continue;
        stats.funcoes += 1;
        for (const m of f.Modelos || []) {
            if (m?.modelo == null) continue;
            stats.modelos += 1;
            if (hasSchema(m)) stats.comSchema += 1;
            else stats.semSchema += 1;
        }
    }
    return stats;
}

function buildMetrics(node, levelIdx, theme) {
    const stats = collectStats(node, levelIdx);
    const accent = theme.palette.colors.green.light;
    const muted = theme.palette.text.disabled;
    const funcaoColor = getLevelColor(theme, 1);
    const modeloColor = getLevelColor(theme, 2);

    if (LEVELS[levelIdx].isLeaf) {
        const ok = stats.comSchema > 0;
        return [
            {
                tooltip: ok
                    ? "Modelo com dashboard schema definido"
                    : "Modelo sem dashboard schema",
                icon: ok ? (
                    <DataObject sx={{ fontSize: 18 }} />
                ) : (
                    <DataObjectOutlined sx={{ fontSize: 18 }} />
                ),
                color: ok ? accent : muted,
            },
        ];
    }

    const metrics = [];
    if (levelIdx === 0) {
        metrics.push({
            tooltip: `${stats.funcoes} funções cadastradas`,
            icon: <Category sx={{ fontSize: 18 }} />,
            value: stats.funcoes,
            color: stats.funcoes > 0 ? funcaoColor : muted,
        });
    }
    metrics.push({
        tooltip: `${stats.modelos} modelos cadastrados`,
        icon: <DeviceHub sx={{ fontSize: 18 }} />,
        value: stats.modelos,
        color: stats.modelos > 0 ? modeloColor : muted,
    });
    metrics.push({
        tooltip: `${stats.comSchema} modelos com dashboard schema`,
        icon: <DataObject sx={{ fontSize: 18 }} />,
        value: stats.comSchema,
        color: stats.comSchema > 0 ? accent : muted,
    });
    metrics.push({
        tooltip: `${stats.semSchema} modelos sem dashboard schema`,
        icon: <DataObjectOutlined sx={{ fontSize: 18 }} />,
        value: stats.semSchema,
        color: stats.semSchema > 0 ? theme.palette.error.main : muted,
    });
    return metrics;
}

export default function HierarchyNode({
    node,
    levelIdx,
    parentId,
    ctx,
    connector,
}) {
    const level = LEVELS[levelIdx];
    const theme = useTheme();

    const labelValue = node?.[level.name];
    const [editValue, setEditValue] = useState(labelValue ?? "");
    const [createValue, setCreateValue] = useState("");

    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    if (labelValue === null || labelValue === undefined) return null;

    const nodeId = String(node.id);
    const isExpanded = ctx.expandedIds.includes(nodeId);
    const children = level.childrenKey ? node[level.childrenKey] || [] : [];
    const visibleChildren = level.childrenKey
        ? children.filter((c) => c?.[LEVELS[levelIdx + 1].name] !== null)
        : [];

    const isSelected = level.isLeaf
        ? ctx.selectedId === node?.Predefinicoes?.id
        : levelIdx === 0
            ? ctx.selectedFabricanteId === node.id
            : levelIdx === 1
                ? ctx.selectedFuncaoId === node.id
                : false;
    const childSelected =
        !level.isLeaf && hasSelectedDescendant(node, levelIdx, ctx.selectedId);

    /* ---------------- API ---------------- */

    const handleEdit = async () => {
        const text = editValue.trim();
        if (!text) return;
        try {
            const resp = await api.put(
                level.apiUpdate,
                level.apiUpdateBody(text, node.id)
            );
            if (resp.data?.status === "Error") {
                ctx.notify(`Erro ao editar ${level.editLabel}`, "error");
            } else {
                ctx.notify("Dados salvos com sucesso", "success");
                ctx.mutators.updateNode(levelIdx, node.id, {
                    [level.name]: text,
                });
            }
        } catch (e) {
            ctx.notify("Erro de conexão com o servidor", "error");
        }
    };

    const handleDelete = async () => {
        try {
            const resp = await api.post(level.apiDelete, { id: node.id });
            setOpenDeleteModal(false);
            if (resp.data?.status === "Error") {
                ctx.notify(`Erro ao excluir ${level.editLabel}`, "error");
            } else {
                ctx.notify("Dados salvos com sucesso", "success");
                ctx.mutators.removeNode(levelIdx, node.id);
                if (level.isLeaf && isSelected) ctx.clearSelection();
            }
        } catch (e) {
            setOpenDeleteModal(false);
            ctx.notify("Erro de conexão com o servidor", "error");
        }
    };

    const handleCreateChild = async () => {
        const text = createValue.trim();
        if (!text) return;
        try {
            const resp = await api.post(
                level.apiCreateChild,
                level.apiCreateChildBody(text, node.id)
            );
            if (resp.data?.status === "Error") {
                ctx.notify(
                    `Erro ao criar ${level.childCreateLabel}`,
                    "error"
                );
            } else {
                ctx.notify("Dados salvos com sucesso", "success");
                const novo = level.transformNewChild(resp.data);
                ctx.mutators.addChild(levelIdx, node.id, novo);
                ctx.expand(nodeId);
                // se o filho criado é uma Modelo, atualiza seleção e predefinicoes
                if (LEVELS[levelIdx + 1].isLeaf) {
                    ctx.createPredefinicoes(novo.Predefinicoes);
                    ctx.selectModelo(novo.Predefinicoes?.id, novo.modelo);
                }
            }
        } catch (e) {
            ctx.notify("Erro de conexão com o servidor", "error");
        } finally {
            setCreateValue("");
        }
    };

    /* ---------------- Render ---------------- */

    const actions = [];
    if (!level.isLeaf && levelIdx !== 0 && levelIdx !== 1) {
        // "Adicionar Função" e "Adicionar Modelo" foram movidos para o modo
        // de edição dos sumários (Fabricante/Função). Esta entrada continua
        // disponível apenas para níveis intermediários adicionais (futuro).
        actions.push({
            label: `Adicionar ${level.childCreateLabel}`,
            icon: <Add color="primary" fontSize="small" />,
            onClick: () => setOpenCreateModal(true),
            divider: true,
        });
    }
    actions.push(
        {
            label: `Editar ${level.editLabel}`,
            icon: <Edit color="primary" fontSize="small" />,
            onClick: () => {
                setEditValue(labelValue);
                setOpenEditModal(true);
            },
        },
        {
            label: `Excluir ${level.editLabel}`,
            icon: <Delete color="error" fontSize="small" />,
            onClick: () => setOpenDeleteModal(true),
            color: "error",
        }
    );

    const onCardClick = () => {
        // Expansão/recolhimento ocorre apenas ao clicar no botão de seta
        // (CardSelection.onToggleExpand). Clicar no card apenas seleciona.
        if (level.isLeaf) {
            ctx.selectModelo(node.Predefinicoes.id, labelValue);
        } else if (levelIdx === 0) {
            ctx.selectFabricante?.(node.id);
        } else if (levelIdx === 1) {
            ctx.selectFuncao?.(node.id);
        }
    };

    return (
        <Box
            sx={
                connector
                    ? { position: "relative", pl: 2 }
                    : undefined
            }
        >
            {/* Tronco vertical contínuo: vai do topo deste bloco até o
                bottom (para que se conecte ao próximo irmão). No último
                irmão, ele é omitido — apenas o "cotovelo" curto da caixa
                interna desce até o centro do card. */}
            {connector && !connector.isLast && (
                <Box
                    sx={{
                        position: "absolute",
                        left: 0,
                        top: "-4px",
                        bottom: "-4px",
                        borderLeft: `3px solid ${getNodeColor(theme)}`,
                    }}
                />
            )}

            {/* Caixa interna que envolve apenas o CARD — usada para
                ancorar o cotovelo (trecho vertical curto do topo até o
                centro do card) e a linha horizontal no centro do card. */}
            <Box sx={connector ? { position: "relative" } : undefined}>
                {connector && (
                    <>
                        <Box
                            sx={{
                                position: "absolute",
                                left: "-16px",
                                top: "-4px",
                                height: "calc(50% + 4px)",
                                borderLeft: `3px solid ${getNodeColor(theme)}`,
                            }}
                        />
                        <Box
                            sx={{
                                position: "absolute",
                                left: "-16px",
                                top: "50%",
                                width: "24px",
                                borderTop: `3px solid ${getNodeColor(theme)}`,
                            }}
                        />
                    </>
                )}
                <CardSelection
                    title={labelValue}
                    isChild={levelIdx > 0}
                    hasChildren={!level.isLeaf && visibleChildren.length > 0}
                    isExpanded={isExpanded}
                    onToggleExpand={() => ctx.toggleExpand(nodeId)}
                    selected={isSelected}
                    childSelected={childSelected}
                    onClick={onCardClick}
                    actions={actions}
                    metrics={buildMetrics(node, levelIdx, theme)}
                    accentColor={getNodeColor(theme)}
                />
            </Box>

            {!level.isLeaf && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box
                        sx={{
                            ml: connector ? 4.5 : 2.5,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {children.map((child, index) => (
                            <HierarchyNode
                                key={child.id ?? `placeholder-${index}`}
                                node={child}
                                levelIdx={levelIdx + 1}
                                parentId={node.id}
                                ctx={ctx}
                                connector={{
                                    isLast: index === children.length - 1,
                                }}
                            />
                        ))}
                    </Box>
                </Collapse>
            )}

            {/* Modal: criar filho */}
            {!level.isLeaf && (
                <Dialog
                    open={openCreateModal}
                    onClose={() => setOpenCreateModal(false)}
                >
                    <DialogTitle color="primary">
                        Adicionar {level.childCreateLabel}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            variant="standard"
                            label={level.childCreateLabel}
                            value={createValue}
                            autoFocus
                            onChange={(e) => setCreateValue(e.target.value)}
                            sx={{ width: "240px" }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleCreateChild();
                                    setOpenCreateModal(false);
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color="secondary"
                            onClick={() => {
                                setOpenCreateModal(false);
                                setCreateValue("");
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            color="primary"
                            onClick={() => {
                                handleCreateChild();
                                setOpenCreateModal(false);
                            }}
                        >
                            Confirmar
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Modal: editar */}
            <Dialog
                open={openEditModal}
                onClose={() => setOpenEditModal(false)}
            >
                <DialogTitle color="primary">
                    Editar {level.editLabel.toLowerCase()}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        variant="standard"
                        label={level.editLabel}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        sx={{ width: "240px" }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleEdit();
                                setOpenEditModal(false);
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        color="secondary"
                        onClick={() => {
                            setOpenEditModal(false);
                            setEditValue(labelValue);
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        color="primary"
                        onClick={() => {
                            handleEdit();
                            setOpenEditModal(false);
                        }}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal: excluir */}
            <InternoModalConfirmacaoSaida
                openConfirmacao={openDeleteModal}
                setOpenConfirmacao={setOpenDeleteModal}
                title={`Excluir ${level.editLabel}`}
                contentText={level.deleteText}
                confirmText="Excluir"
                severity="error"
                execute={handleDelete}
            />
        </Box>
    );
}
